import { Stack } from "@mui/system";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Box, Button, Card, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import BludataDefaultMainHeader from "../../../UI/BludataDefaultMainHeader";
import { BluAlert, BluLoader } from "@bludata/components";
import UtenteMaster from "../../../interfaces/utenteMaster";
import fetchListaUtentiMaster from "../../../api/utenteMaster/fetchListaUtentiMaster";
import deleteUtenteMaster from "../../../api/utenteMaster/deleteUtenteMaster";
import { customAlphabet } from "nanoid";
import fetchGruppo from "../../../api/gruppi/fetchGruppo";
import Gruppo from "../../../interfaces/gruppo";
import setInviaResetPassword from "../../../api/utenteMaster/setInviaResetPassword";
import UsersDataGrid from "./components/UsersDataGrid";
import DialogModificaUtente from "./DialogModificaUtente";

export const ConfigurazioneUtenti = memo(() => {
  const numberAlphabet = "0123456789";
  const { loginData } = useSelector((state: RootState) => state.root);
  const [userList, setUsers] = useState<{
    currentPage: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    users: UtenteMaster[];
  }>();
  const [gruppoList, setGruppoList] = useState<Gruppo[]>([]);
  const [loading, setLoading] = useState(false);
  const gridApiRef = useGridApiRef();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<{
    eMail: string;
    user: string;
  }>();
  const [selectedUtente, setSelectedUtente] = useState<UtenteMaster | null>(
    null
  );
  const [dialogUtente, setDialogUtente] = useState<{
    utente: UtenteMaster;
    isEdit: boolean;
  } | null>(null);

  const reloadUser = () => {
    if (loginData?.admin) {
      setLoading(true);
      fetchListaUtentiMaster(page + 1, 10)
        .then((res) => {
          setLoading(false);

          // Preserve selection if possible
          if (selectedUtente) {
            const stillExists = res.users.find(
              (item) => item.id === selectedUtente.id
            );
            if (!stillExists) {
              setSelectedUtente(null);
            } else {
              // Update selected user with fresh data
              setSelectedUtente(stillExists);
            }
          }

          setUsers(res);
        })
        .catch((err) => {
          setError(err || "Internal Server Error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const reloadGroups = () => {
    if (loginData?.admin) {
      setLoading(true);
      fetchGruppo()
        .then((res) => {
          setLoading(false);
          setGruppoList(res);
        })
        .catch((err) => {
          setError(err || "Internal Server Error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    reloadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onNuovoClick = () => {
    const id = Number(customAlphabet(numberAlphabet, 10)());
    const newUtente: UtenteMaster = {
      id: id,
      gruppo: "",
      descrizioneGruppo: "",
      user: "",
      password: "",
      isAdmin: false,
      nomeUtente: "",
      eMail: "",
      attivo: false,
      token: "",
      scadenzaToken: "",
      create: true,
    };
    setDialogUtente({
      utente: newUtente,
      isEdit: false,
    });
  };

  const onEditClick = (utente: UtenteMaster) => {
    setDialogUtente({
      utente,
      isEdit: true,
    });
  };

  const onDeleteClick = (utenteId: number) => {
    setConfirmDelete(utenteId);
  };

  const handleDeleteConfirm = () => {
    if (confirmDelete === null) return;

    setLoading(true);
    deleteUtenteMaster(confirmDelete)
      .then(() => {
        setSuccess("Utente eliminato con successo.");
        reloadUser();
      })
      .catch((err) => {
        setError(
          err?.response?.data?.response || "Errore durante l'eliminazione"
        );
      })
      .finally(() => {
        setLoading(false);
        setConfirmDelete(null);
      });
  };

  const onDialogClose = (savedItemId?: number) => {
    // Keep track of the ID to select after reload
    const idToSelect =
      savedItemId ||
      (dialogUtente?.isEdit ? dialogUtente.utente.id : undefined);

    reloadUser();
    setDialogUtente(null);

    // Select the newly created or edited user after reload
    if (idToSelect) {
      setTimeout(() => {
        fetchListaUtentiMaster(page + 1, 10).then((result) => {
          const utenteToSelect = result.users.find((u) => u.id === idToSelect);
          if (utenteToSelect) {
            setSelectedUtente(utenteToSelect);
          }
        });
      }, 300); // Small delay to ensure data is loaded
    }
  };

  const onResetPasswordClick = () => {
    if (!selectedUtente) return;
    setResetPasswordUser({
      eMail: selectedUtente.eMail,
      user: selectedUtente.user,
    });
  };

  const handleResetPasswordConfirm = () => {
    if (!resetPasswordUser) return;

    setInviaResetPassword(resetPasswordUser.eMail, resetPasswordUser.user)
      .then(() => {
        setSuccess("Mail di Reset Password inviata con successo.");
      })
      .catch((err) => {
        setError(
          err?.response?.data?.response || "Errore nella fase di invio email"
        );
      })
      .finally(() => {
        setResetPasswordUser(undefined);
      });
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        backgroundColor: "var(--hovered-background-color)",
      }}
    >
      {dialogUtente && (
        <DialogModificaUtente
          onClose={onDialogClose}
          utente={dialogUtente.utente}
          gruppoList={gruppoList}
          isEdit={dialogUtente.isEdit}
        />
      )}
      <BluAlert
        show={!!success}
        handleClose={() => {
          setSuccess("");
        }}
        text={success}
        type="success"
      />
      <BluAlert
        show={!!error}
        handleClose={() => {
          setError("");
        }}
        text={error}
        type="error"
      />
      <BluAlert
        show={!!resetPasswordUser}
        handleClose={() => {
          setResetPasswordUser(undefined);
        }}
        handleConfirm={handleResetPasswordConfirm}
        text={
          "Stai per inviare una mail al cliente per resettare la password. Sei sicuro di voler procedere?"
        }
        title="Attenzione"
        type="warning"
      />
      <BluAlert
        show={confirmDelete !== null}
        handleClose={() => {
          setConfirmDelete(null);
        }}
        handleConfirm={handleDeleteConfirm}
        text={
          "Sei sicuro di voler eliminare questo utente da Catalogo Bludata?"
        }
        type="warning"
      />
      <Stack flexDirection="column" flex={1} sx={{ height: "100%" }}>
        <BludataDefaultMainHeader
          showReload={true}
          text={"Configurazione / Utenti"}
          onRefreshTap={reloadUser}
        />
        <Card variant="outlined" sx={{ height: "100%", p: 1, m: 0.5, mt: 0 }}>
          <Stack direction="column" spacing={1} sx={{ height: "100%" }}>
            <BluLoader open={loading} />
            <Typography>
              In questa sezione è possibile configurare gli utenti. I campi Nome
              utente, Gruppo, Email e User sono obbligatori.
            </Typography>
            {loginData?.admin && (
              <>
                <UsersDataGrid
                  gridApiRef={gridApiRef}
                  userList={
                    userList || {
                      currentPage: 0,
                      pageSize: 0,
                      totalItems: 0,
                      totalPages: 0,
                      users: [],
                    }
                  }
                  page={page}
                  onPageChange={setPage}
                  onRowClick={setSelectedUtente}
                  selectedUtente={selectedUtente}
                />
              </>
            )}
            <Stack direction={"row"} justifyContent={"end"} spacing={0.5}>
              <Button
                variant="contained"
                color="primary"
                onClick={onNuovoClick}
              >
                Nuovo
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => selectedUtente && onEditClick(selectedUtente)}
                disabled={!selectedUtente}
              >
                Modifica
              </Button>
              {selectedUtente && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onResetPasswordClick}
                  disabled={!selectedUtente}
                >
                  Reset Password
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  selectedUtente &&
                  selectedUtente.id &&
                  onDeleteClick(selectedUtente.id)
                }
                disabled={!selectedUtente}
              >
                Elimina
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
});
